import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Menu, X } from 'lucide-react';
import { Logo } from '@/components/ui/Logo';
import { AuthHeader } from '@/components/AuthHeader';
import { BillingStateBanner } from '@/components/BillingStateBanner';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { AppSidebar } from './AppSidebar';
import { AuthenticatedFooter } from './AuthenticatedFooter';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { trackRouteView, debounce } from '@/utils/analytics';

interface AppLayoutProps {
  children: React.ReactNode;
}

/**
 * Unified layout for all authenticated user pages (/app/*)
 * Features:
 * - Persistent sidebar (desktop) with collapse
 * - Mobile drawer navigation
 * - Role-aware menu filtering
 * - Tier-gated feature indicators
 */
export const AppLayout = ({ children }: AppLayoutProps) => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem('app-sidebar-collapsed');
    return saved === 'true';
  });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { user } = useAuth();
  
  // Debounced route tracking to avoid spam
  const trackRouteViewDebounced = useRef(
    debounce((route: string, userId: string, role?: string, orgId?: string) => {
      trackRouteView(route, userId, role, orgId);
    }, 500)
  ).current;
  
  // Track route views
  useEffect(() => {
    if (user && location.pathname.startsWith('/app/')) {
      trackRouteViewDebounced(
        location.pathname,
        user.id,
        user.highestRole,
        user.organization?.id
      );
    }
  }, [location.pathname, user, trackRouteViewDebounced]);

  const toggleSidebar = () => {
    const newState = !sidebarCollapsed;
    setSidebarCollapsed(newState);
    localStorage.setItem('app-sidebar-collapsed', String(newState));
  };

  return (
    <div className="min-h-screen bg-sand flex flex-col">
      {/* Top Header */}
      <header className="bg-midnight-green/95 backdrop-blur-sm border-b border-deep-teal sticky top-0 z-50 shadow-lg shadow-black/20">
        <div className="flex justify-between items-center h-16 px-4 lg:px-6">
          {/* Left: Mobile Menu + Logo */}
          <div className="flex items-center gap-3">
            {/* Mobile Menu Toggle */}
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="lg:hidden text-sand hover:text-copper"
                >
                  <Menu className="h-5 w-5" />
                  <span className="sr-only">Open menu</span>
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-64 p-0">
                <div className="flex items-center justify-between p-4 border-b">
                  <Logo variant="horizontal" size="sm" />
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <X className="h-5 w-5" />
                  </Button>
                </div>
                <AppSidebar 
                  collapsed={false} 
                  onNavigate={() => setMobileMenuOpen(false)} 
                />
              </SheetContent>
            </Sheet>

            {/* Logo */}
            <Link to="/app/dashboard" className="hidden lg:block">
              <Logo variant="horizontal" size="md" className="brightness-0 invert" />
            </Link>
            <Link to="/app/dashboard" className="lg:hidden">
              <Logo variant="horizontal" size="sm" className="brightness-0 invert" />
            </Link>
          </div>

          {/* Right: Auth Header */}
          <AuthHeader />
        </div>
      </header>

      {/* Billing Banner */}
      <BillingStateBanner />

      {/* Main Content Area */}
      <div className="flex flex-1">
        {/* Desktop Sidebar */}
        <aside
          className={cn(
            "hidden lg:flex flex-col border-r bg-card transition-all duration-300",
            sidebarCollapsed ? "w-16" : "w-64"
          )}
        >
          {/* Collapse Toggle */}
          <div className="flex items-center justify-end p-2 border-b">
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleSidebar}
              className="h-10 w-10 p-2 hover:bg-accent focus-visible:ring-2 focus-visible:ring-emerald-500"
              aria-label={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
              aria-expanded={!sidebarCollapsed}
            >
              {sidebarCollapsed ? (
                <ChevronRight className="h-5 w-5" aria-hidden="true" />
              ) : (
                <ChevronLeft className="h-5 w-5" aria-hidden="true" />
              )}
            </Button>
          </div>

          {/* Sidebar Navigation */}
          <AppSidebar collapsed={sidebarCollapsed} />
        </aside>

        {/* Page Content */}
        <main className="flex-1 flex flex-col min-w-0">
          <div className="flex-1">
            {children}
          </div>
          <AuthenticatedFooter />
        </main>
      </div>
    </div>
  );
};
